/**
 * Danh sách cabin — NGUỒN DUY NHẤT cho cả trang đặt lịch của học viên
 * (`CalcomPage`) lẫn trang quản lý của admin (`AdminCalcomPage`).
 *
 * Trước đây mảng này được chép tay ở cả hai file. Hai bản giống hệt nhau, và
 * không có gì buộc chúng phải giống nhau. Thêm cabin mà chỉ nhớ một file thì
 * học viên đặt được lịch trong khi admin không thấy cabin đó để huỷ hay đánh
 * vắng — mỗi file tự nó vẫn đúng, build vẫn xanh, và không có gì báo sai.
 *
 * ── `id` là gì và lấy ở đâu ──
 * Là `eventTypeId` bên Cal.com, và nó thuộc về TÀI KHOẢN Cal.com của chính
 * cabin đó — mỗi cabin một khoá API riêng. Lấy bằng:
 *     GET https://api.cal.com/v2/event-types
 *     Authorization: Bearer <khoá của cabin>
 *     cal-api-version: 2024-06-14
 *
 * Phải là CHUỖI: nơi gọi so bằng `cabin.id === eventId?.toString()`, để số thì
 * mọi phép so đều trượt và trang lặng lẽ không tìm thấy cabin nào.
 *
 * ── `serviceKey` là gì ──
 * Khoá tra cứu sang bản ghi trong collection `calcoms` của uniapp-backend, nơi
 * giữ khoá API (đã mã hoá). Sai một ký tự thì backend không tra ra cấu hình và
 * trả lỗi xác thực — nên khi thêm cabin, tạo bản ghi DB TRƯỚC rồi mới thêm vào
 * đây.
 *
 * ── Thứ tự có ý nghĩa ──
 * `AdminCalcomPage` lấy phần tử đầu làm lựa chọn mặc định. Đảo thứ tự là đổi
 * cabin mặc định của admin mà không ai chủ ý.
 */

/**
 * Khu vực địa lý của cabin. Học viên hay đến nhầm: ba máy Dĩ An ở cùng một cơ
 * sở, còn Phú Giáo cách đó hàng chục cây số — nên `khuVuc` là thứ quyết định
 * có bật cảnh báo hay không, thay vì cắm cứng id của Phú Giáo. Thêm máy thứ
 * hai ở Phú Giáo thì nó tự được cảnh báo.
 */
export const KHU_VUC_CHINH = 'di-an';

/** Tên khu vực để ghép vào câu cảnh báo — đừng để lọt chuỗi kebab ra giao diện. */
export const TEN_KHU_VUC = {
  'di-an': 'Dĩ An',
  'phu-giao': 'Phú Giáo',
};

export const DANH_SACH_CABIN = [
  // Ba máy Dĩ An đứng liền nhau vì chúng cùng một cơ sở — xen Phú Giáo vào
  // giữa thì người chọn phải đọc lại cả danh sách mới thấy hết máy Dĩ An.
  { id: '4895881', name: 'Cabin Dĩ An Máy 1', serviceKey: 'cabin-di-an-1', khuVuc: 'di-an' },
  { id: '4508649', name: 'Cabin Dĩ An Máy 2', serviceKey: 'cabin-di-an-2', khuVuc: 'di-an' },
  // `moTuNgay`: cabin chưa nhận lịch trước ngày này. Máy 3 mới lắp, bắt đầu
  // nhận từ 04/10/2026. Dạng 'yyyy-MM-dd' để so sánh trực tiếp với `dateKey`
  // mà `fetchSlots` đang dùng — đổi định dạng thì phép so bằng chuỗi sẽ sai.
  { id: '7263230', name: 'Cabin Dĩ An 3', serviceKey: 'cabin-di-an-3', khuVuc: 'di-an', moTuNgay: '2026-10-04' },
  { id: '4896118', name: 'Cabin Phú Giáo', serviceKey: 'cabin-phu-giao', khuVuc: 'phu-giao' },
];

/**
 * Tra cabin theo eventTypeId. Nhận cả chuỗi lẫn số — Cal.com trả kiểu số trong
 * payload booking, còn `<Form.Select>` thì luôn cho ra chuỗi.
 */
export function timCabinTheoId(eventId) {
  if (eventId === null || eventId === undefined || eventId === '') return undefined;
  return DANH_SACH_CABIN.find((c) => c.id === String(eventId));
}

/**
 * Địa chỉ hiện cho học viên sau khi đặt lịch.
 *
 * Để cùng chỗ với danh sách cabin vì hai thứ này phải đi kèm nhau: thêm cabin
 * mà quên địa chỉ thì học viên đặt được lịch rồi không biết đi đâu. Test
 * `moi cabin deu co dia chi` khoá đúng điều đó.
 *
 * Dĩ An 1, 2, 3 là ba máy trong CÙNG một cơ sở nên dùng chung một địa chỉ
 * (chủ hệ thống xác nhận 29/09/2026).
 */
const DIA_CHI_DI_AN =
  'Số 14/4 Đường ống nước thô D1800, Khu phố Tân Hòa, Phường Đông Hòa, TP. Hồ Chí Minh.';

export const DIA_CHI_CABIN = {
  '4895881': `CABIN DĨ AN 1: ${DIA_CHI_DI_AN}`,
  '4508649': `CABIN DĨ AN 2: ${DIA_CHI_DI_AN}`,
  '4896118': 'CABIN PHÚ GIÁO: Ấp Nước Vàng, Phú Giáo, Bình Dương',
  '7263230': `CABIN DĨ AN 3: ${DIA_CHI_DI_AN}`,
};

/** Tên để hiện lên bảng. Giữ nguyên chuỗi "Không xác định" của bản cũ. */
export function tenCabinTheoId(eventId) {
  return timCabinTheoId(eventId)?.name || 'Không xác định';
}

/**
 * Cabin này có nằm ngoài cơ sở chính không — tức có cần bắt học viên xác nhận
 * đã biết địa chỉ khác hay không.
 *
 * Suy ra từ `khuVuc`. Đây không phải chuyện thẩm mỹ: cắm cứng id Phú Giáo thì
 * lần thêm máy thứ hai ở đó, cảnh báo sẽ im lặng không bật, và không có gì báo
 * sai cho tới khi có học viên đi nhầm.
 *
 * Id lạ trả `false`: chưa chọn được cabin thì chưa có gì để cảnh báo.
 */
export function canhBaoKhacDiaChi(eventId) {
  const c = timCabinTheoId(eventId);
  return !!c && c.khuVuc !== KHU_VUC_CHINH;
}

/** Tên khu vực của một cabin, để dựng câu cảnh báo. */
export function tenKhuVucTheoId(eventId) {
  const c = timCabinTheoId(eventId);
  return c ? (TEN_KHU_VUC[c.khuVuc] || c.khuVuc) : '';
}

/**
 * Ngày `dateKey` có nằm TRƯỚC mốc mở của cabin không.
 *
 * Khác với `ngay_hoc_hih` (mốc riêng của từng học viên, lấy từ CRM): đây là mốc
 * của chính cái máy — máy chưa lắp xong thì không ai đặt được, bất kể học viên
 * là ai.
 *
 * So sánh chuỗi `yyyy-MM-dd` theo thứ tự từ điển, đúng vì định dạng cố định độ
 * dài. Cabin không khai `moTuNgay` thì không bao giờ bị chặn.
 *
 * @param {string} dateKey dạng 'yyyy-MM-dd'
 */
export function cabinChuaMo(eventId, dateKey) {
  const c = timCabinTheoId(eventId);
  if (!c || !c.moTuNgay || !dateKey) return false;
  return String(dateKey) < c.moTuNgay;
}

/** Mốc mở dạng dd/MM/yyyy để hiện cho học viên; '' nếu cabin không có mốc. */
export function ngayMoCuaCabin(eventId) {
  const c = timCabinTheoId(eventId);
  if (!c || !c.moTuNgay) return '';
  const [y, m, d] = c.moTuNgay.split('-');
  return `${d}/${m}/${y}`;
}
